import {
  Box,
  Button,
  Collapse,
  TextField,
  Typography,
  CircularProgress,
  useTheme,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ErrorIcon from "@mui/icons-material/Error";
import InfoIcon from "@mui/icons-material/Info";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { useState, useMemo, useEffect, useRef } from "react";
import { usePagination } from "@/hooks/usePagination";
import { formatJSONWithSyntax, formatTime } from "@/utils/debugpanelUtils";

interface ConsoleLog {
  message: string | any[] | object;
  level: string;
  timestamp: number;
  originalIndex: number;
}

interface ConsoleTabProps {
  devToolsData: any[];
  selectedTab: string;
  currentVideoTime?: number;
  recordingStartTime?: number;
  isFromVideoPreview?: boolean;
  onSeekToTime?: (timeInSeconds: number) => void;
}

const ConsoleTab = ({ devToolsData, selectedTab, currentVideoTime, recordingStartTime = 0, isFromVideoPreview = false, onSeekToTime }: ConsoleTabProps) => {
  const theme = useTheme();
  const isPublicBugPage = window?.location?.pathname.includes('public/bug');

  // Theme colors based on public/private context
  const themeColors = useMemo(() => {
    if (isPublicBugPage) {
      return {
        containerBg: "#ffffff",
        filterBarBg: "#f9f9f9",
        borderColor: "#e0e0e0",
        textPrimary: "#333333",
        textSecondary: "#5f6368",
        inputBg: "#ffffff",
        inputBorder: "#dadce0",
        logBgNormal: "#f8f9fa",
        logBgWarning: "#fff8e1",
        logBorderColor: "#f1f3f4",
        errorTagBg: "#fce4ec",
        errorTagBgActive: "#e91e63",
        errorTagColor: "#e91e63",
        activityTagBg: "#e3f2fd",
        activityTagBgActive: "#2196f3",
        activityTagColor: "#2196f3",
        linkColor: "#1976d2",
        logTextColor: "#795548",
      };
    }
    return {
      containerBg: theme.palette.background.paper,
      filterBarBg: theme.palette.background.default,
      borderColor: theme.palette.divider,
      textPrimary: theme.palette.text.primary,
      textSecondary: theme.palette.text.secondary,
      inputBg: theme.palette.background.paper,
      inputBorder: theme.palette.divider,
      logBgNormal: theme.palette.background.default,
      logBgWarning: theme.palette.mode === 'dark' ? '#3d3000' : "#fff8e1",
      logBorderColor: theme.palette.divider,
      errorTagBg: theme.palette.mode === 'dark' ? '#3d1a2a' : "#fce4ec",
      errorTagBgActive: "#e91e63",
      errorTagColor: "#e91e63",
      activityTagBg: theme.palette.mode === 'dark' ? '#1a3d5c' : "#e3f2fd",
      activityTagBgActive: "#2196f3",
      activityTagColor: "#2196f3",
      linkColor: theme.palette.primary.main,
      logTextColor: theme.palette.text.primary,
    };
  }, [isPublicBugPage, theme]);
  const [expandedLogs, setExpandedLogs] = useState({});
  const [filterText, setFilterText] = useState("");
  const [consoleFilterTag, setConsoleFilterTag] = useState("all");
  const containerRef = useRef<HTMLDivElement>(null);
  const activeLogRef = useRef<HTMLDivElement>(null);
  const lastScrolledIndex = useRef<number>(-1);

  const filteredConsoleLogs = useMemo(() => {
    return (
      devToolsData
        ?.map((log: any, index: number) => {
          const tagMatch =
            consoleFilterTag === "all" ||
            (consoleFilterTag === "errors" && log.level === "error") ||
            (consoleFilterTag === "userActivity" && log.level === "log");

          const textMatch =
            !filterText ||
            (typeof log.message === "string" &&
              log.message.toLowerCase().includes(filterText.toLowerCase())) ||
            (Array.isArray(log.message) &&
              log.message.some((item) =>
                String(item).toLowerCase().includes(filterText.toLowerCase())
              ));

          return tagMatch && textMatch
            ? { ...log, originalIndex: index }
            : null;
        })
        .filter((log: any) => log !== null) || []
    );
  }, [devToolsData, filterText, consoleFilterTag]);

  const { visibleItems, isAtBottom, loadMore, isLoading } =
    usePagination<ConsoleLog>({
      data: filteredConsoleLogs,
      initialCount: 20,
      loadCount: 10,
    });

  // Use all filtered logs when in video preview mode for proper sync
  const effectiveLogs = isFromVideoPreview ? filteredConsoleLogs : visibleItems;

  // Calculate the earliest console timestamp for video seeking
  // Console logs might use absolute timestamps (like action logs)
  const earliestConsoleTimestamp = useMemo(() => {
    if (!devToolsData?.length) return 0;
    let earliest = Infinity;
    devToolsData.forEach((log: any) => {
      const t = typeof log.timestamp === 'number'
        ? log.timestamp
        : new Date(log.timestamp).getTime();
      if (!isNaN(t) && t < earliest) {
        earliest = t;
      }
    });
    return earliest === Infinity ? 0 : earliest;
  }, [devToolsData]);

  // Handle click on a console log to seek video
  const handleLogClick = (log: ConsoleLog) => {
    if (!onSeekToTime) return;

    const logTimeMs = typeof log.timestamp === 'number'
      ? log.timestamp
      : new Date(log.timestamp).getTime();

    if (isNaN(logTimeMs)) return;

    // Check if timestamps are absolute (large values > year 2000) or relative (small values)
    const isAbsoluteTimestamp = earliestConsoleTimestamp > 1000000000000; // > year 2001 in ms

    let relativeTimeSec: number;
    if (isAbsoluteTimestamp) {
      // Use earliest console timestamp as base for absolute timestamps
      relativeTimeSec = (logTimeMs - earliestConsoleTimestamp) / 1000;
    } else {
      // Use recordingStartTime for relative timestamps
      relativeTimeSec = (logTimeMs - recordingStartTime) / 1000;
    }

    if (relativeTimeSec >= 0) {
      onSeekToTime(relativeTimeSec);
    }
  };

  // Find the active log index based on current video time
  const activeLogIndex = useMemo(() => {
    if (currentVideoTime === undefined || !filteredConsoleLogs.length) return -1;

    // Current video time is in seconds
    const currentTimeSec = currentVideoTime;

    // Find the log closest to current time (but not after it)
    let closestIndex = -1;
    let closestDiff = Infinity;

    for (let i = 0; i < filteredConsoleLogs.length; i++) {
      const log = filteredConsoleLogs[i];
      // Convert timestamp to milliseconds (handle string dates, Date objects, or numbers)
      const logTimeMs = typeof log.timestamp === 'number'
        ? log.timestamp
        : new Date(log.timestamp).getTime();

      if (isNaN(logTimeMs)) continue;

      // Calculate relative time from recording start (in seconds)
      const logRelativeTimeSec = (logTimeMs - recordingStartTime) / 1000;

      // Calculate difference (log should be at or before current time)
      const diff = currentTimeSec - logRelativeTimeSec;

      // Log should be at or before current time, and closest to it
      if (diff >= 0 && diff < closestDiff) {
        closestDiff = diff;
        closestIndex = log.originalIndex;
      }
    }

    return closestIndex;
  }, [currentVideoTime, recordingStartTime, filteredConsoleLogs]);

  // Auto-scroll to the active log when it changes
  useEffect(() => {
    if (activeLogIndex === -1 || activeLogIndex === lastScrolledIndex.current) return;
    if (!activeLogRef.current || !containerRef.current) return;

    // Only scroll if this is a different log
    lastScrolledIndex.current = activeLogIndex;

    // Scroll the active log into view
    activeLogRef.current.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });
  }, [activeLogIndex]);

  const toggleLogExpanded = (index) => {
    setExpandedLogs((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const shouldBeExpandable = (content) => {
    return (
      (typeof content === "string" && content.length > 150) ||
      typeof content === "object" ||
      Array.isArray(content)
    );
  };

  const renderContent = (content, isExpanded, index) => {
    let displayContent = content;
    let isString = typeof content === "string";

    if (!isString) {
      displayContent = JSON.stringify(content, null, 2);
    }

    const maxLength = 150;
    const shouldTruncate =
      isString && displayContent.length > maxLength && !isExpanded;

    return (
      <>
        <span
          dangerouslySetInnerHTML={{
            __html: formatJSONWithSyntax(
              shouldTruncate
                ? displayContent.slice(0, maxLength)
                : displayContent
            ),
          }}
        />
        {shouldTruncate && <span>...</span>}
        {shouldBeExpandable(content) && (
          <Button
            variant="text"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              toggleLogExpanded(index);
            }}
            sx={{
              color: "#1976d2",
              textTransform: "none",
              fontSize: "0.75rem",
              p: 0,
              ml: 1,
              minWidth: 0,
            }}
          >
            {isExpanded ? "Show less" : "Show more"}
          </Button>
        )}
      </>
    );
  };

  return (
    <Box
      sx={{
        display: selectedTab === "console" ? "flex" : "none",
        flexDirection: "column",
        height: "100%",
        overflow: "hidden",
        width: "100%",
        bgcolor: themeColors.containerBg,
      }}
    >
      {/* Console Filter Bar */}
      <Box
        sx={{
          p: 1,
          display: "flex",
          alignItems: "center",
          borderBottom: `1px solid ${themeColors.borderColor}`,
          bgcolor: themeColors.filterBarBg,
        }}
      >
        <Box
          sx={{
            position: "relative",
            flex: 1,
            display: "flex",
            alignItems: "center",
          }}
        >
          <SearchIcon
            sx={{
              position: "absolute",
              left: 8,
              color: themeColors.textSecondary,
              fontSize: "1rem",
            }}
          />
          <TextField
            placeholder="Filter"
            size="small"
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
            sx={{
              "& .MuiOutlinedInput-root": {
                pl: 4,
                height: 32,
                fontSize: "0.85rem",
                bgcolor: themeColors.inputBg,
                color: themeColors.textPrimary,
              },
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: themeColors.inputBorder,
              },
              width: "250px",
            }}
            variant="outlined"
          />
        </Box>
      </Box>

      {/* Filter Tags */}
      <Box
        sx={{
          display: "flex",
          p: 0.5,
          bgcolor: themeColors.filterBarBg,
        }}
      >
        <Box
          sx={{
            m: 0.5,
            px: 1,
            py: 0.5,
            bgcolor: consoleFilterTag === "errors" ? themeColors.errorTagBgActive : themeColors.errorTagBg,
            borderRadius: "4px",
            fontSize: "0.75rem",
            display: "flex",
            alignItems: "center",
            color: consoleFilterTag === "errors" ? "#fff" : themeColors.errorTagColor,
            cursor: "pointer",
            transition: "all 0.2s ease",
            "&:hover": {
              bgcolor: consoleFilterTag === "errors" ? "#c2185b" : "#f8bbd0",
            },
          }}
          onClick={() =>
            setConsoleFilterTag(
              consoleFilterTag === "errors" ? "all" : "errors"
            )
          }
        >
          <ErrorIcon sx={{ fontSize: "0.9rem", mr: 0.5 }} />
          Network Errors
        </Box>
        <Box
          sx={{
            m: 0.5,
            px: 1,
            py: 0.5,
            bgcolor:
              consoleFilterTag === "userActivity" ? themeColors.activityTagBgActive : themeColors.activityTagBg,
            borderRadius: "4px",
            fontSize: "0.75rem",
            display: "flex",
            alignItems: "center",
            color: consoleFilterTag === "userActivity" ? "#fff" : themeColors.activityTagColor,
            cursor: "pointer",
            transition: "all 0.2s ease",
            "&:hover": {
              bgcolor:
                consoleFilterTag === "userActivity" ? "#1976d2" : "#bbdefb",
            },
          }}
          onClick={() =>
            setConsoleFilterTag(
              consoleFilterTag === "userActivity" ? "all" : "userActivity"
            )
          }
        >
          <InfoIcon sx={{ fontSize: "0.9rem", mr: 0.5 }} />
          User Activity
        </Box>
      </Box>

      {/* Console Logs */}
      <Box
        ref={containerRef}
        sx={{
          flex: 1,
          overflow: "auto",
          fontFamily: "Consolas, monospace",
          fontSize: "0.8rem",
          position: "relative",
        }}
      >
        {effectiveLogs.length === 0 ? (
          <Typography sx={{ p: 2, color: themeColors.textSecondary }} fontSize={"14px"}>
            No console logs found.
            {filterText && " Try changing your filter."}
            {consoleFilterTag !== "all" &&
              " Or try a different console filter."}
          </Typography>
        ) : (
          effectiveLogs.map((log: ConsoleLog) => {
            const originalIndex = log.originalIndex;
            const isExpanded = expandedLogs[originalIndex];
            const isExpandable = shouldBeExpandable(log.message);
            const isActive = activeLogIndex === originalIndex;

            return (
              <Box
                key={originalIndex}
                ref={isActive ? activeLogRef : null}
                onClick={() => handleLogClick(log)}
                sx={{
                  bgcolor: isActive
                    ? (isPublicBugPage ? "rgba(103, 58, 183, 0.15)" : "rgba(103, 58, 183, 0.25)")
                    : log.level === "warn" || log.level === "error"
                      ? themeColors.logBgWarning
                      : themeColors.logBgNormal,
                  borderBottom: `1px solid ${themeColors.logBorderColor}`,
                  borderLeft: isActive ? "3px solid #673ab7" : "3px solid transparent",
                  minHeight: "50px",
                  transition: "background-color 0.2s ease, border-left-color 0.2s ease",
                  cursor: onSeekToTime ? "pointer" : "default",
                  "&:hover": onSeekToTime ? {
                    bgcolor: isActive
                      ? (isPublicBugPage ? "rgba(103, 58, 183, 0.2)" : "rgba(103, 58, 183, 0.3)")
                      : log.level === "warn" || log.level === "error"
                        ? themeColors.logBgWarning
                        : "action.hover",
                  } : {},
                }}
              >
                {/* Log entry header */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    p: 1,
                    cursor: isExpandable ? "pointer" : "default",
                  }}
                  onClick={() =>
                    isExpandable && toggleLogExpanded(originalIndex)
                  }
                >
                  {/* Expand/collapse icon */}
                  {isExpandable && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: "20px",
                        mr: 1,
                        color: themeColors.textSecondary,
                      }}
                    >
                      {isExpanded ? (
                        <KeyboardArrowDownIcon sx={{ fontSize: "1rem" }} />
                      ) : (
                        <KeyboardArrowRightIcon sx={{ fontSize: "1rem" }} />
                      )}
                    </Box>
                  )}

                  {/* Timestamp */}
                  <Box
                    sx={{
                      minWidth: "80px",
                      mr: 1,
                      fontSize: "0.7rem",
                      color: themeColors.textSecondary,
                      pt: 0.25,
                    }}
                  >
                    {formatTime(log.timestamp)}
                  </Box>

                  {/* Level icon */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "20px",
                      mx: isExpandable ? 0 : 1,
                      color:
                        log.level === "error"
                          ? "#e91e63"
                          : log.level === "warn"
                          ? "#ff9800"
                          : themeColors.textSecondary,
                    }}
                  >
                    {log.level === "error"
                      ? "⊗"
                      : log.level === "warn"
                      ? "⚠"
                      : "ℹ"}
                  </Box>

                  {/* Log content */}
                  <Box
                    sx={{
                      flex: 1,
                      overflow: "hidden",
                      fontFamily: "Consolas, monospace",
                      color: themeColors.logTextColor,
                      pr: 2,
                    }}
                  >
                    {renderContent(log.message, isExpanded, originalIndex)}
                  </Box>
                </Box>

                {/* Expanded view for objects/arrays */}
                {isExpandable && isExpanded && (
                  <Collapse in={isExpanded}>
                    <Box
                      sx={{
                        pl: 8,
                        pr: 2,
                        pb: 1,
                        fontFamily: "Consolas, monospace",
                        fontSize: "0.8rem",
                        backgroundColor:
                          log.level === "warn" || log.level === "error"
                            ? themeColors.logBgWarning
                            : themeColors.logBgNormal,
                      }}
                    >
                      <Box
                        component="pre"
                        sx={{
                          margin: 0,
                          fontFamily: "Consolas, monospace",
                          fontSize: "0.8rem",
                          color: themeColors.logTextColor,
                          whiteSpace: "pre-wrap",
                        }}
                      >
                        <span
                          dangerouslySetInnerHTML={{
                            __html: formatJSONWithSyntax(
                              typeof log.message === "string"
                                ? log.message
                                : JSON.stringify(log.message, null, 2)
                            ),
                          }}
                        />
                      </Box>
                    </Box>
                  </Collapse>
                )}
              </Box>
            );
          })
        )}

        {/* Bottom load button - hide in video preview mode */}
        {!isFromVideoPreview && !isAtBottom && effectiveLogs.length > 0 && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              p: 1,
              borderTop: `1px solid ${themeColors.logBorderColor}`,
            }}
          >
            <Button
              variant="outlined"
              size="small"
              onClick={loadMore}
              disabled={isLoading}
              sx={{ fontSize: "0.75rem" }}
            >
              {isLoading ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
              Load More
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ConsoleTab;
